// panelStack.ts — the summoned-surface FOCUS / Z-ORDER raise (the pop-under fix).
// CONTRACT: docs/ux/SHELL-COMPOSITION.md §2 (summoned map-overlay surfaces, map-primacy)
//
// THE PROBLEM: the shell's summoned overlay surfaces (the LeftSlideout, the C3
// PanelHost docks) were pinned to STATIC z-indexes, so opening one while another
// was up could paint it UNDERNEATH — "clicked and it popped under, feels broken".
//
// THE LAW here: the LAST surface the operator opened OR clicked is the FRONT
// surface. Every participating panel takes its z-index from this store instead of
// a hardcoded constant; `raise(key)` moves that panel to the top of the stack (and
// marks it FOCUSED, which drives the teal focus hairline). Raising is idempotent
// for the already-top panel (no redundant emit / re-render churn).
//
// THE BAND: panels stack inside a FIXED z band [PANEL_Z_BASE .. PANEL_Z_MAX]
// reserved between the resting map chrome (.panel z10 / cartouche z11) below and
// the structural shell furniture above (banners z30, the raised left-gutter rail
// layer z44 whose summoned popovers must always beat a panel — see
// gutterframe.css), the copilot dock (z42) and the modal tier (graph z60 / trust
// z62 / toasts z900 / settings z1000). The band is never exceeded: the stack is
// pruned on drop and re-normalized, and z is clamped at PANEL_Z_MAX.
//
// Like heavyPanel.ts this is a TINY framework-agnostic store (no React import
// needed by non-hook callers, no host, no MapLibre): raw `raisePanel` /
// `dropPanel` / `subscribePanels`, plus the ergonomic `usePanelLayer(key)` React
// binding for the shell components.

/** Bottom of the reserved summoned-panel z band (above .panel z10/cartouche z11). */
export const PANEL_Z_BASE = 20;
/** Top of the band (below the banner tier z30 / copilot z42 / rail layer z44). */
export const PANEL_Z_MAX = 29;

/** A participating surface's stable identity (e.g. "slideout", "dock:cap/panel"). */
export type PanelLayerKey = string;

// --- the store: an ordered bottom→top stack of live panel keys -----------------

let stack: PanelLayerKey[] = [];
const listeners = new Set<() => void>();

function emit(): void {
  // Snapshot so a listener that (un)subscribes mid-dispatch can't mutate the
  // set we're iterating (same defensive pattern as heavyPanel.ts).
  for (const fn of [...listeners]) fn();
}

/** The z-index for `key`: its position in the stack, clamped to the band. A key
 *  that never raised sits at the band base (it still paints above map chrome). */
export function panelZ(key: PanelLayerKey): number {
  const i = stack.indexOf(key);
  if (i < 0) return PANEL_Z_BASE;
  return Math.min(PANEL_Z_BASE + i, PANEL_Z_MAX);
}

/** The key currently on top (the FOCUSED panel), or null when none is raised. */
export function topPanel(): PanelLayerKey | null {
  return stack.length > 0 ? stack[stack.length - 1] : null;
}

/** Bring `key` to the FRONT of the panel band (called on open and on click).
 *  Idempotent for the already-top key — no emit, no re-render churn. */
export function raisePanel(key: PanelLayerKey): void {
  if (topPanel() === key) return;
  stack = stack.filter((k) => k !== key);
  stack.push(key);
  emit();
}

/** Remove `key` from the stack (a closed/unmounted panel must not hold focus —
 *  the next-most-recent panel becomes the front). No-op if absent. */
export function dropPanel(key: PanelLayerKey): void {
  if (!stack.includes(key)) return;
  stack = stack.filter((k) => k !== key);
  emit();
}

/** Subscribe to stack changes. Returns an unsubscribe fn. Not invoked on
 *  subscribe (read `panelZ` / `topPanel` for the initial value). */
export function subscribePanels(fn: () => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

// --- the React binding ---------------------------------------------------------

import { useCallback, useEffect, useState } from "react";

/** What `usePanelLayer` hands a participating surface. */
export interface PanelLayerControls {
  /** The surface's current z-index (inline `style={{ zIndex: z }}`). */
  z: number;
  /** True iff this surface is the front of the panel band — drives the teal
   *  focus hairline (`data-focused`). */
  focused: boolean;
  /** Bring this surface to the front (wire to open + pointerdown-capture). */
  raise: () => void;
  /** Retire this surface from the stack (wire to close/unmount). */
  drop: () => void;
}

/**
 * Bind a summoned overlay surface into the shared focus/z-order band.
 *
 *   const layer = usePanelLayer("slideout");
 *   useEffect(() => { open ? layer.raise() : layer.drop(); }, [open]);
 *   <aside style={{ zIndex: layer.z }} data-focused={layer.focused ? "" : undefined}
 *          onPointerDownCapture={layer.raise} …>
 *
 * The hook re-renders the owner on every stack change so z/focused stay live.
 * On unmount it drops the key (a dead surface must not pin the front slot).
 */
export function usePanelLayer(key: PanelLayerKey): PanelLayerControls {
  const [, setTick] = useState(0);

  useEffect(() => {
    const unsub = subscribePanels(() => setTick((t) => t + 1));
    return () => {
      unsub();
      dropPanel(key);
    };
  }, [key]);

  const raise = useCallback(() => raisePanel(key), [key]);
  const drop = useCallback(() => dropPanel(key), [key]);

  return { z: panelZ(key), focused: topPanel() === key, raise, drop };
}
